import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Gear, FloppyDisk, Plus, Trash, Check, Medal, Trophy, Star, PencilSimple, X, Calculator, HandCoins } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);

export function CommissionsPanel({ year, month }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("calc");
  const [commissions, setCommissions] = useState([]);
  const [manualComms, setManualComms] = useState([]);
  const [config, setConfig] = useState(null);
  const [bonusTypes, setBonusTypes] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [showBonusForm, setShowBonusForm] = useState(false);
  const [bonusForm, setBonusForm] = useState({ user_id: "", user_name: "", bonus_type_id: "", bonus_type_name: "", amount: "", description: "", date: new Date().toISOString().slice(0, 10) });
  const [newType, setNewType] = useState({ name: "", description: "", color: "#3B82F6" });
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({ user_id: "", user_name: "", amount: "", description: "", concept: "comision", date: new Date().toISOString().slice(0, 10) });
  const [editManualId, setEditManualId] = useState(null);

  const selectedMonth = month || new Date().getMonth() + 1;
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [cRes, cfgRes, btRes, bRes, uRes, mcRes] = await Promise.all([
        axios.get(`${API}/admin/commissions`, { params: { year, month: selectedMonth }, headers }),
        axios.get(`${API}/admin/commission-config`, { headers }),
        axios.get(`${API}/admin/bonus-types`, { headers }),
        axios.get(`${API}/admin/bonuses`, { params: { year, month: selectedMonth }, headers }),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/manual-commissions`, { params: { year, month: selectedMonth }, headers }),
      ]);
      setCommissions(cRes.data);
      setConfig(cfgRes.data);
      setBonusTypes(btRes.data);
      setBonuses(bRes.data);
      setUsers(uRes.data || []);
      setManualComms(mcRes.data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [year, selectedMonth, token]);

  useEffect(() => { load(); }, [load]);

  const saveTiers = async () => {
    try {
      await axios.put(`${API}/admin/commission-config`, config, { headers });
      toast.success("Tiers guardados"); load();
    } catch { toast.error("Error"); }
  };

  const updateTier = (idx, field, val) => {
    const newTiers = [...config.tiers];
    newTiers[idx] = { ...newTiers[idx], [field]: parseFloat(val) || 0 };
    setConfig({ ...config, tiers: newTiers });
  };

  const addTier = () => setConfig({ ...config, tiers: [...config.tiers, { min_percent: 0, max_percent: 100, commission_rate: 5 }] });
  const removeTier = (idx) => setConfig({ ...config, tiers: config.tiers.filter((_, i) => i !== idx) });

  const addBonusType = async () => {
    if (!newType.name) return;
    try {
      await axios.post(`${API}/admin/bonus-types`, newType, { headers });
      toast.success("Tipo creado"); setNewType({ name: "", description: "", color: "#3B82F6" }); load();
    } catch { toast.error("Error"); }
  };

  const deleteBonusType = async (id) => {
    try { await axios.delete(`${API}/admin/bonus-types/${id}`, { headers }); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  const handleBonusUserChange = (uid) => {
    const u = users.find(x => x.id === uid);
    setBonusForm(p => ({ ...p, user_id: uid, user_name: u?.name || "" }));
  };

  const handleBonusTypeChange = (tid) => {
    const t = bonusTypes.find(x => x.id === tid);
    setBonusForm(p => ({ ...p, bonus_type_id: tid, bonus_type_name: t?.name || "" }));
  };

  const saveBonus = async () => {
    if (!bonusForm.user_id || !bonusForm.amount) { toast.error("Completa los campos"); return; }
    try {
      await axios.post(`${API}/admin/bonuses`, { ...bonusForm, amount: parseFloat(bonusForm.amount) }, { headers });
      toast.success("Bono asignado"); setShowBonusForm(false);
      setBonusForm({ user_id: "", user_name: "", bonus_type_id: "", bonus_type_name: "", amount: "", description: "", date: new Date().toISOString().slice(0, 10) });
      load();
    } catch { toast.error("Error"); }
  };

  const deleteBonus = async (id) => {
    try { await axios.delete(`${API}/admin/bonuses/${id}`, { headers }); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  // Manual commission functions
  const resetManualForm = () => {
    setManualForm({ user_id: "", user_name: "", amount: "", description: "", concept: "comision", date: new Date().toISOString().slice(0, 10) });
    setEditManualId(null);
  };

  const handleManualUserChange = (uid) => {
    const u = users.find(x => x.id === uid);
    setManualForm(p => ({ ...p, user_id: uid, user_name: u?.name || "" }));
  };

  const saveManualComm = async () => {
    if (!manualForm.user_id || !manualForm.amount) { toast.error("Completa empleado y monto"); return; }
    try {
      if (editManualId) {
        await axios.put(`${API}/admin/manual-commissions/${editManualId}`, { ...manualForm, amount: parseFloat(manualForm.amount) }, { headers });
        toast.success("Comision actualizada");
      } else {
        await axios.post(`${API}/admin/manual-commissions`, { ...manualForm, amount: parseFloat(manualForm.amount) }, { headers });
        toast.success("Comision registrada");
      }
      setShowManualForm(false); resetManualForm(); load();
    } catch { toast.error("Error"); }
  };

  const startEditManual = (item) => {
    setManualForm({ user_id: item.user_id, user_name: item.user_name, amount: item.amount, description: item.description || "", concept: item.concept || "comision", date: item.date });
    setEditManualId(item.id);
    setShowManualForm(true);
  };

  const deleteManualComm = async (id) => {
    try { await axios.delete(`${API}/admin/manual-commissions/${id}`, { headers }); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  const MONTHS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  const totalComm = commissions.reduce((s, c) => s + c.commission_amount, 0);
  const totalManual = manualComms.reduce((s, c) => s + (c.amount || 0), 0);
  const totalBonuses = bonuses.reduce((s, b) => s + (b.amount || 0), 0);

  const CONCEPTS = [
    { id: "comision", label: "Comision" },
    { id: "ajuste", label: "Ajuste" },
    { id: "retroactivo", label: "Retroactivo" },
    { id: "especial", label: "Especial" },
    { id: "otro", label: "Otro" },
  ];

  const subTabs = [
    { id: "calc", label: "Automaticas", icon: Calculator },
    { id: "manual", label: "Manuales", icon: HandCoins },
    { id: "bonuses", label: "Bonos", icon: Medal },
    ...(isAdmin ? [{ id: "config", label: "Configuracion", icon: Gear }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="commissions-panel">
      {/* Summary badges */}
      <div className="flex gap-2 flex-wrap">
        <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg">Automaticas: {fmt(totalComm)}</span>
        <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-3 py-1.5 rounded-lg">Manuales: {fmt(totalManual)}</span>
        <span className="text-xs font-semibold text-purple-700 bg-purple-50 px-3 py-1.5 rounded-lg">Bonos: {fmt(totalBonuses)}</span>
        <span className="text-xs font-bold text-slate-900 bg-slate-100 px-3 py-1.5 rounded-lg">Total: {fmt(totalComm + totalManual + totalBonuses)}</span>
        <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-3 py-1.5 rounded-lg">{MONTHS[selectedMonth - 1]} {year}</span>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`comm-subtab-${t.id}`}>
            <t.icon size={13} />
            {t.label}
          </button>
        ))}
      </div>

      {/* === AUTO COMMISSIONS TABLE === */}
      {tab === "calc" && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Empleado</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Rol</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Meta</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Ingreso</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">% Meta</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">% Comision</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Comision</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Tickets</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {commissions.filter(c => c.commission_amount > 0 || c.income > 0).map((c, i) => (
                  <tr key={c.user_id} className="hover:bg-slate-50" data-testid={`comm-row-${i}`}>
                    <td className="px-3 py-2.5 font-medium text-slate-700 flex items-center gap-2">
                      {i < 3 && <Trophy size={14} className="text-amber-500" />}
                      {c.user_name}
                    </td>
                    <td className="px-3 py-2.5"><span className="px-2 py-0.5 bg-slate-100 text-xs rounded-full">{c.role}</span></td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{fmt(c.goal)}</td>
                    <td className="px-3 py-2.5 text-right font-medium text-emerald-700">{fmt(c.income)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${c.goal_percent >= 100 ? "bg-emerald-100 text-emerald-700" : c.goal_percent >= 80 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {c.goal_percent}%
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center font-bold text-blue-700">{c.commission_rate}%</td>
                    <td className="px-3 py-2.5 text-right font-bold text-blue-700">{fmt(c.commission_amount)}</td>
                    <td className="px-3 py-2.5 text-center text-slate-500">{c.tickets_closed}</td>
                  </tr>
                ))}
                {commissions.filter(c => c.commission_amount > 0 || c.income > 0).length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Sin datos de comisiones automaticas para este periodo</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === MANUAL COMMISSIONS === */}
      {tab === "manual" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-teal-700 bg-teal-50 px-3 py-1.5 rounded-lg">Total Manual: {fmt(totalManual)}</span>
            <button onClick={() => { resetManualForm(); setShowManualForm(true); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-1.5" data-testid="add-manual-comm-btn">
              <Plus size={16} /> Registrar Comision
            </button>
          </div>

          {showManualForm && (
            <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="manual-comm-form">
              <h3 className="font-semibold text-slate-700">{editManualId ? "Editar Comision" : "Nueva Comision Manual"}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Empleado *</label>
                  <select value={manualForm.user_id} onChange={e => handleManualUserChange(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="manual-user">
                    <option value="">Seleccionar...</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Concepto</label>
                  <select value={manualForm.concept} onChange={e => setManualForm(p => ({ ...p, concept: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="manual-concept">
                    {CONCEPTS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Monto *</label>
                  <input type="number" value={manualForm.amount} onChange={e => setManualForm(p => ({ ...p, amount: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="manual-amount" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Fecha</label>
                  <input type="date" value={manualForm.date} onChange={e => setManualForm(p => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="manual-date" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Descripcion</label>
                <input value={manualForm.description} onChange={e => setManualForm(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="Detalle de la comision" data-testid="manual-desc" />
              </div>
              <div className="flex gap-2">
                <button onClick={saveManualComm} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700" data-testid="manual-save-btn">
                  {editManualId ? "Actualizar" : "Registrar"}
                </button>
                <button onClick={() => { setShowManualForm(false); resetManualForm(); }} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200">Cancelar</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Empleado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Concepto</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Descripcion</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Monto</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Registrado por</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Acc.</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {manualComms.map(mc => (
                  <tr key={mc.id} className="hover:bg-slate-50" data-testid={`manual-row-${mc.id}`}>
                    <td className="px-4 py-3 text-slate-600">{mc.date}</td>
                    <td className="px-4 py-3 font-medium text-slate-700">{mc.user_name}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs rounded-full capitalize">{mc.concept || "comision"}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{mc.description}</td>
                    <td className="px-4 py-3 text-right font-semibold text-teal-700">{fmt(mc.amount)}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{mc.created_by_name}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => startEditManual(mc)} className="p-1 hover:bg-blue-50 rounded text-blue-600"><PencilSimple size={14} /></button>
                        {isAdmin && <button onClick={() => deleteManualComm(mc.id)} className="p-1 hover:bg-red-50 rounded text-red-500"><Trash size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {manualComms.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Sin comisiones manuales registradas</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === BONUSES === */}
      {tab === "bonuses" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-purple-700 bg-purple-50 px-3 py-1.5 rounded-lg">Total Bonos: {fmt(totalBonuses)}</span>
            {isAdmin && (
              <button onClick={() => setShowBonusForm(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center gap-1.5" data-testid="add-bonus-btn">
                <Plus size={16} /> Asignar Bono
              </button>
            )}
          </div>

          {showBonusForm && (
            <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="bonus-form">
              <h3 className="font-semibold text-slate-700">Asignar Bono</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Empleado *</label>
                  <select value={bonusForm.user_id} onChange={e => handleBonusUserChange(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="bonus-user">
                    <option value="">Seleccionar...</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Tipo de Bono</label>
                  <select value={bonusForm.bonus_type_id} onChange={e => handleBonusTypeChange(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="bonus-type">
                    <option value="">Seleccionar...</option>
                    {bonusTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Monto *</label>
                  <input type="number" value={bonusForm.amount} onChange={e => setBonusForm(p => ({ ...p, amount: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="bonus-amount" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Fecha</label>
                  <input type="date" value={bonusForm.date} onChange={e => setBonusForm(p => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Descripcion</label>
                <input value={bonusForm.description} onChange={e => setBonusForm(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="bonus-desc" />
              </div>
              <div className="flex gap-2">
                <button onClick={saveBonus} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="bonus-save-btn">Guardar</button>
                <button onClick={() => setShowBonusForm(false)} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200">Cancelar</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Empleado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Descripcion</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Monto</th>
                  {isAdmin && <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Acc.</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {bonuses.map(b => (
                  <tr key={b.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600">{b.date}</td>
                    <td className="px-4 py-3 font-medium text-slate-700">{b.user_name}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">{b.bonus_type_name || "General"}</span></td>
                    <td className="px-4 py-3 text-slate-500">{b.description}</td>
                    <td className="px-4 py-3 text-right font-semibold text-purple-700">{fmt(b.amount)}</td>
                    {isAdmin && <td className="px-4 py-3 text-center"><button onClick={() => deleteBonus(b.id)} className="p-1 hover:bg-red-50 rounded text-red-500"><Trash size={14} /></button></td>}
                  </tr>
                ))}
                {bonuses.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Sin bonos registrados</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === CONFIG === */}
      {tab === "config" && isAdmin && config && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2"><Gear size={18} /> Tiers de Comision</h3>
              <div className="flex gap-2">
                <button onClick={addTier} className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs hover:bg-slate-200 flex items-center gap-1"><Plus size={12} />Agregar</button>
                <button onClick={saveTiers} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 flex items-center gap-1" data-testid="save-tiers-btn"><FloppyDisk size={12} />Guardar</button>
              </div>
            </div>
            <p className="text-xs text-slate-400">Define los rangos de % de meta cumplida y el % de comision correspondiente</p>
            <div className="space-y-2">
              {config.tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg">
                  <span className="text-xs text-slate-500 w-8">#{i + 1}</span>
                  <input type="number" value={t.min_percent} onChange={e => updateTier(i, "min_percent", e.target.value)} className="w-20 px-2 py-1 border rounded text-sm text-center" />
                  <span className="text-xs text-slate-400">% a</span>
                  <input type="number" value={t.max_percent} onChange={e => updateTier(i, "max_percent", e.target.value)} className="w-20 px-2 py-1 border rounded text-sm text-center" />
                  <span className="text-xs text-slate-400">% de meta =</span>
                  <input type="number" value={t.commission_rate} onChange={e => updateTier(i, "commission_rate", e.target.value)} className="w-16 px-2 py-1 border rounded text-sm text-center font-bold text-blue-700" />
                  <span className="text-xs text-slate-500">% comision</span>
                  <button onClick={() => removeTier(i)} className="p-1 text-red-400 hover:text-red-600 ml-auto"><Trash size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border p-5 space-y-4">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2"><Medal size={18} /> Tipos de Bonos</h3>
            <div className="flex gap-2">
              <input value={newType.name} onChange={e => setNewType(p => ({ ...p, name: e.target.value }))} placeholder="Nombre del bono" className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="bonus-type-name" />
              <input value={newType.description} onChange={e => setNewType(p => ({ ...p, description: e.target.value }))} placeholder="Descripcion" className="flex-1 px-3 py-2 border rounded-lg text-sm" />
              <input type="color" value={newType.color} onChange={e => setNewType(p => ({ ...p, color: e.target.value }))} className="w-10 h-10 rounded border cursor-pointer" />
              <button onClick={addBonusType} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="add-bonus-type-btn">Agregar</button>
            </div>
            <div className="space-y-2">
              {bonusTypes.map(t => (
                <div key={t.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                    <span className="text-sm font-medium">{t.name}</span>
                    {t.description && <span className="text-xs text-slate-400">- {t.description}</span>}
                  </div>
                  <button onClick={() => deleteBonusType(t.id)} className="text-red-500 text-xs hover:underline">Eliminar</button>
                </div>
              ))}
              {bonusTypes.length === 0 && <p className="text-sm text-slate-400">Sin tipos de bonos creados</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
